import React, { useState } from 'react';

import YatLoader from '@pkg/components/Sections/Profile/Loader';

import { AssetsImageCacheMaxWidth, AssetsVideoCacheMaxWidth } from '@app/client/common/structs/assets/assets';
import { getMashedUrl } from '@app/helpers';

import { NFTAssetError } from '../NFTAssetError';

import { AssetHandler } from './components';
import { isBlackListed, NFT_ASSET_VIDEO_FORMATS } from './utilities';

import { NFTAssetLoading } from './styles';

export interface NFTAssetProps {
    imageUrl?: string;
    preventAutoPlay?: boolean;
    videoUrl?: string;
    fallback?: string;
    hideLoadingMarkup?: boolean;
    errorSquareBackground?: boolean;
    isSold?: boolean;
    displayVideo?: boolean;
    videoControls?: boolean;
    noErrorText?: boolean;
    objectFit?: 'contain' | 'cover';
    disableLoadingAnimation?: boolean;
    onError?(): void;
}
export function NFTAsset({
    imageUrl,
    videoUrl,
    hideLoadingMarkup,
    fallback,
    noErrorText,
    displayVideo,
    preventAutoPlay,
    // TODO: We should not default to `true` on boolean props.
    errorSquareBackground = true,
    isSold = false,
    videoControls = false,
    onError,
    objectFit = 'cover',
    disableLoadingAnimation,
}: NFTAssetProps) {
    const [loaded, setLoaded] = useState(hideLoadingMarkup);

    function handleLoaded() {
        setLoaded(true);
    }

    const mashedImageUrl = getMashedUrl({
        width: AssetsImageCacheMaxWidth.POD_NFT_CARD,
        url: imageUrl,
    });

    const mashedVideoUrl = getMashedUrl({
        url: videoUrl,
        width: AssetsVideoCacheMaxWidth.POD_NFT_CARD,
    });

    if (isBlackListed(mashedVideoUrl) && isBlackListed(mashedImageUrl)) {
        return <NFTAssetError noErrorText={noErrorText} errorSquareBackground={errorSquareBackground} />;
    }

    const loadingMarkup = loaded ? null : (
        <NFTAssetLoading>
            <YatLoader show disableLoadingAnimation={disableLoadingAnimation} />
        </NFTAssetLoading>
    );

    const imageUrlIsVideo = NFT_ASSET_VIDEO_FORMATS.some((format) => imageUrl?.endsWith(`.${format}`));

    const formattedVideoUrl = imageUrlIsVideo ? mashedImageUrl : mashedVideoUrl;
    const _videoUrl = displayVideo ? formattedVideoUrl : undefined;

    return (
        <>
            {loadingMarkup}

            <AssetHandler
                autoPlay={!preventAutoPlay}
                ignoreVideoError
                imageUrl={mashedImageUrl}
                videoUrl={_videoUrl}
                fallback={fallback || videoUrl || imageUrl}
                controls={videoControls}
                errorSquareBackground={errorSquareBackground}
                noErrorText={noErrorText}
                isSold={isSold}
                onError={onError}
                onLoad={handleLoaded}
                objectFit={objectFit}
            />
        </>
    );
}
